'use client';

import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { zodResolver } from '@hookform/resolvers/zod';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { type MessageDescriptor, useIntl } from 'react-intl';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { type ReactNode, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { CheckCreditCheckDetailDataDocument } from '@packages/gql/generated/shopping/CheckCreditCheckDetailDataDocument';
import { LivingSituationCard } from './LivingSituationCard';
import { AdditionalCostsCard } from './AdditionalCostsCard';

import { FurtherInformationCard } from './FurtherInformationCard';
import { creditCheckFormSchema } from './schema';
import { type Kind, type CreditCheckFormValues, type TiltFormSchema } from './schema';
import { ShortFormSection } from './ShortFormSection';
import { LongFormSection } from './LongFormSection';
import type { OpenDialogHandler } from './types';
import { creditFormMessages as messages } from './messages';
import { CreditContractSection } from './CreditContractSection';

/* GraphQL */ `
  mutation checkCreditCheckDetailData(
    $id: String!
    $automobileFollowUpQuestion: Boolean
    $automobileQuestion: Boolean
    $childDayCareQuestion: Boolean
    $loanFromFamily: Int
    $monthlyCosts: Int!
    $monthlyIncome: Int!
    $monthlyLoanCosts: Int
    $numberOfChildren: Int!
    $numberOfPeople: Int!
    $otherLoans: Int
    $savingsRetirementQuestion: Boolean
    $studentLoan: Int
    $typeOfPropertyOwnership: TypeOfPropertyOwnership
  ) {
    checkCreditCheckDetailData(
      id: $id
      automobileFollowUpQuestion: $automobileFollowUpQuestion
      automobileQuestion: $automobileQuestion
      childDayCareQuestion: $childDayCareQuestion
      loanFromFamily: $loanFromFamily
      monthlyCosts: $monthlyCosts
      monthlyIncome: $monthlyIncome
      monthlyLoanCosts: $monthlyLoanCosts
      numberOfChildren: $numberOfChildren
      numberOfPeople: $numberOfPeople
      otherLoans: $otherLoans
      savingsRetirementQuestion: $savingsRetirementQuestion
      studentLoan: $studentLoan
      typeOfPropertyOwnership: $typeOfPropertyOwnership
    ) {
      allowed
      rate
    }
  }
`;

type CreditCheckFormProps = {
  kind: Kind;
  rate?: string;
  id?: string;
  returnUrl: string;
};

type DialogState = {
  dialogTitle: string | ReactNode[];
  dialogContent: string | ReactNode[];
  open: boolean;
  showButtons?: boolean;
};
const RequiredHintParagraph: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <p>
      <span className="text-size-body2">{formatMessage(messages.requiredHint)}</span>
    </p>
  );
};
export const CreditCheckForm: React.FC<CreditCheckFormProps> = ({
  kind,
  returnUrl,
  rate: rateParam,
  id,
}) => {
  const { formatMessage } = useIntl();
  const [formStep, setFormStep] = useState(kind === 'long' ? 1 : 3);
  const [rate, setRate] = useState(rateParam);
  const [dialogState, setDialogState] = useState<DialogState>({
    dialogTitle: '',
    dialogContent: '',
    open: false,
  });
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [, checkCreditCheck] = useAuthMutation(CheckCreditCheckDetailDataDocument);

  useEffect(() => {
    const params = new URLSearchParams(searchParams?.toString());
    if (kind === 'short') {
      params.set('iltForm', 'optIn');
    } else if (formStep === 1) {
      params.set('iltForm', 'LongIlt1');
    } else if (formStep === 2) {
      params.set('iltForm', 'LongIlt2');
    } else if (formStep === 3) {
      params.set('iltForm', 'LongOk');
    }
    replace(`${pathname}?${params.toString()}`);
    // adding the useRouters replace to the dependency array will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStep, kind, pathname, searchParams]);

  const { trigger, control, setValue, formState, getValues, handleSubmit, ...restMethods } =
    useForm<CreditCheckFormValues<typeof kind>>({
      resolver: zodResolver(creditCheckFormSchema(formatMessage, kind)),
      mode: 'all',
      defaultValues: {
        firstPartForm: {
          numberOfPeople: undefined,
          numberOfChildren: undefined,
          monthlyCosts: undefined,
          typeOfPropertyOwnership: undefined,
          loanFromFamily: undefined,
          studentLoan: undefined,
          otherLoans: undefined,
          monthlyLoanCosts: undefined,
        },
        secondPartForm: {
          automobileFollowUpQuestion: undefined,
          automobileQuestion: undefined,
          savingsRetirementQuestion: undefined,
          childDayCareQuestion: undefined,
          monthlyIncome: undefined,
        },
        thirdPartForm: {
          creditContractQuestion: undefined,
          creditInformationChecked: undefined,
        },
        wontDiscloseInformation: false,
      },
    });
  const handleDialogOpen: OpenDialogHandler = (
    dialogTitle: MessageDescriptor,
    dialogContent: MessageDescriptor,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    showButtons?: boolean,
  ) => {
    e?.preventDefault();
    setDialogState({
      dialogTitle: formatMessage(dialogTitle, {
        asterisk: () => '',
      }),
      dialogContent: formatMessage(dialogContent, {
        break: <br />,
        strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
      }),
      open: true,
      showButtons,
    });
  };
  const handleDialogClose = () => {
    setDialogState({ dialogContent: '', dialogTitle: '', open: false });
    setValue('wontDiscloseInformation', false);
  };
  const displayDisclosureError = (checkedState: boolean) => {
    // const wontDiscloseInformation = creditCheckWontDiscloseInformationSchema(formatMessage).parse(
    //  getValues('wontDiscloseInformation'),
    // );
    // we only want to submit if the user is willing to disclose the information bring up a dialog
    if (checkedState) {
      handleDialogOpen(
        messages.dataCollectionDialogTitle,
        messages.dataCollectionDialogContent,
        undefined,
        true,
      );
    }
  };

  const doTheMagic = async (payload: {
    creditContractQuestion?: string;
    wontDiscloseInformation?: boolean;
  }) => {
    try {
      const response = await fetch(returnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.redirected ?? response.status === 302) {
        window.location.href = response.url;
      }
    } catch (e) {
      // this should never happen because the server is redirecting to pay page in case of errors
      // but if it ever happens we can log the error
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onSubmitFirstFormHandler = async () => {
    const triggered = await trigger('firstPartForm');
    const isFormStepOneValid = triggered && !formState.errors.firstPartForm;
    isFormStepOneValid && setFormStep(2);
  };
  const onSubmitSecondFormHandler = async () => {
    const triggered = await trigger('secondPartForm');
    const isFormStepTwoValid = triggered && !formState.errors.secondPartForm;
    if (isFormStepTwoValid) {
      // do the mutation
      checkCreditCheck({
        id: id!, // in credit-check page we are checking if id is a string, so it should be safe to use it here
        automobileFollowUpQuestion:
          getValues('secondPartForm.automobileFollowUpQuestion') === 'yes',
        automobileQuestion: getValues('secondPartForm.automobileQuestion') === 'yes',
        childDayCareQuestion: getValues('secondPartForm.childDayCareQuestion') === 'yes',
        loanFromFamily: Number(getValues('firstPartForm.loanFromFamily')),
        monthlyCosts: Number(getValues('firstPartForm.monthlyCosts')),
        monthlyIncome: Number(getValues('secondPartForm.monthlyIncome')),
        monthlyLoanCosts: Number(getValues('firstPartForm.monthlyLoanCosts')),
        numberOfChildren: Number(getValues('firstPartForm.numberOfChildren')),
        numberOfPeople: Number(getValues('firstPartForm.numberOfPeople')),
        otherLoans: Number(getValues('firstPartForm.otherLoans')),
        savingsRetirementQuestion: getValues('secondPartForm.savingsRetirementQuestion') === 'yes',
        studentLoan: Number(getValues('firstPartForm.studentLoan')),
        typeOfPropertyOwnership: getValues('firstPartForm.typeOfPropertyOwnership'),
      }).then((result) => {
        const creditCheckResult = result.data?.checkCreditCheckDetailData;
        if (creditCheckResult?.allowed && creditCheckResult.rate) {
          setRate(creditCheckResult.rate.toString());
          setFormStep(3);
        } else {
          doTheMagic({});
        }
      });
    }
  };
  const onSubmitThirdFormHandler = async () => {
    const triggered = await trigger('thirdPartForm');
    const isFormStepThreeValid = triggered && !formState.errors.thirdPartForm;
    if (isFormStepThreeValid) {
      const spreadOfValues: Pick<
        TiltFormSchema,
        'wontDiscloseInformation' | 'creditContractQuestion'
      > = {
        wontDiscloseInformation: getValues('wontDiscloseInformation'),
        creditContractQuestion: getValues('thirdPartForm.creditContractQuestion'),
      } as unknown as TiltFormSchema;
      doTheMagic(spreadOfValues);
    }
  };
  const otherPaymentMethod = () => {
    const payload = {
      wontDiscloseInformation: true,
    };
    doTheMagic(payload);
  };

  const onSubmitFormHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formStep === 1) {
      onSubmitFirstFormHandler();
    } else if (formStep === 2) {
      onSubmitSecondFormHandler();
    } else {
      onSubmitThirdFormHandler();
    }
  };

  return (
    <FormProvider
      {...{
        setValue,
        trigger,
        getValues,
        control,
        formState,
        handleSubmit,
        ...restMethods,
      }}
    >
      <form className="flex flex-col gap-1.5" onSubmit={onSubmitFormHandler}>
        <div className="bg-color-grey-light flex flex-col gap-1.5 p-1.5 md:gap-2 md:px-[44px] md:py-3">
          {formStep === 1 && (
            <>
              <h3 className="text-size-body1 font-bold">
                {formatMessage(messages.creditAgreementInfoHeadline)}
              </h3>
              <p className="text-size-body2">{formatMessage(messages.creditAgreementInfoText)}</p>
              <Button
                onClick={(e) =>
                  handleDialogOpen(messages.bkrDialogTitle, messages.bkrDialogText, e)
                }
                variant="link"
                size="base"
                className="text-size-body2 items-center justify-start self-start"
              >
                {formatMessage(messages.bkrInfoLink)}
              </Button>
              <RequiredHintParagraph />
              <LivingSituationCard />
              <AdditionalCostsCard />
            </>
          )}
          {formStep === 2 && (
            <>
              <RequiredHintParagraph />
              <FurtherInformationCard>
                {/* Don't be confused about the naming here we will change it in the future */}
                <ShortFormSection handleOpenDialog={handleDialogOpen} />
                <LongFormSection handleOpenDialog={handleDialogOpen} />
              </FurtherInformationCard>
            </>
          )}
          {formStep === 3 && rate && (
            <>
              <h3 className="text-size-body1 font-bold">
                {formatMessage(messages.creditAgreementInfoHeadline)}
              </h3>
              <p className="text-size-body2">{formatMessage(messages.creditAgreementInfoText)}</p>
              <Button
                onClick={(e) =>
                  handleDialogOpen(messages.bkrDialogTitle, messages.bkrDialogText, e)
                }
                variant="link"
                size="base"
                className="text-size-body2 items-center justify-start self-start"
              >
                {formatMessage(messages.bkrInfoLink)}
              </Button>
              <CreditContractSection rate={rate} />
            </>
          )}
        </div>
        {kind !== 'short' && (
          <Controller
            name="wontDiscloseInformation"
            control={control}
            render={({ field }) => (
              <div className="bg-color-background-base flex items-center p-1.5">
                <Checkbox
                  onCheckedChange={() => {
                    field.onChange(!field.value);
                    displayDisclosureError(!field.value);
                  }}
                  checked={field.value}
                  {...field}
                  className="mr-2"
                  value="wontDiscloseInformation"
                  id="wontDiscloseInformation"
                />
                <Label className="leading-none" htmlFor="wontDiscloseInformation">
                  {formatMessage(messages.wontDiscloseInformation)}
                </Label>
              </div>
            )}
          />
        )}
        <div className="bg-color-background-base flex px-1.5 py-0.5 md:justify-end md:px-0">
          <Button type="submit" className="w-full md:w-[12.5rem]" variant="primary" size="large">
            {formatMessage(messages.nextButton)}
          </Button>
        </div>
      </form>
      <Dialog onClose={() => handleDialogClose()} isOpen={dialogState.open}>
        <DialogHeader onClose={() => handleDialogClose()}>
          {dialogState.dialogTitle || ''}
        </DialogHeader>
        <DialogContent>
          <div className="p-2">{dialogState.dialogContent}</div>
        </DialogContent>
        {dialogState.showButtons && (
          <DialogFooter>
            <Button variant="primary" onClick={() => handleDialogClose()}>
              {formatMessage(messages.dataCollectionDialogPrimaryButton)}
            </Button>
            <Button onClick={() => otherPaymentMethod()}>
              {formatMessage(messages.dataCollectionDialogSecondaryButton)}
            </Button>
          </DialogFooter>
        )}
      </Dialog>
    </FormProvider>
  );
};
